import { FileText } from 'lucide-react';
import InputField from '../../components/auth/InputField';
import SelectField from '../../components/auth/SelectField';

export default function DocumentUploadStep({ formData, onChange }) {
  const documentTypeOptions = ['ghana_card', 'passport', 'drivers_license'];

  const handleFile = async (file, side) => {
    if (!file) return;
    const preview = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.readAsDataURL(file);
    });
    if (side === 'front') {
      onChange('id_card_front', file);
      onChange('idFrontPreview', preview);
    } else {
      onChange('id_card_back', file);
      onChange('idBackPreview', preview);
    }
  };

  // Handle Ghana Card prefix input (GHA)
  const handleGhanaCardPrefixChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3);
    onChange('ghana_card_prefix', value);
  };

  // Handle Ghana Card middle input (9 digits)
  const handleGhanaCardMiddleChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 9);
    onChange('ghana_card_middle', value);
  };

  // Handle Ghana Card suffix input (1 digit)
  const handleGhanaCardSuffixChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 1);
    onChange('ghana_card_suffix', value);
  };

  // Combine Ghana Card parts for display (with hyphens)
  const getFullGhanaCardNumber = () => {
    const prefix = formData.ghana_card_prefix || '';
    const middle = formData.ghana_card_middle || '';
    const suffix = formData.ghana_card_suffix || '';
    
    if (!prefix && !middle && !suffix) return '';
    
    let fullNumber = prefix;
    if (middle) fullNumber += '-' + middle;
    if (suffix) fullNumber += '-' + suffix;
    
    return fullNumber;
  };

  // Get Ghana Card number for API (without hyphens)
  const getGhanaCardNumberForAPI = () => {
    const prefix = formData.ghana_card_prefix || '';
    const middle = formData.ghana_card_middle || '';
    const suffix = formData.ghana_card_suffix || '';
    
    if (!prefix && !middle && !suffix) return '';
    
    return prefix + middle + suffix;
  };

  // Validate Ghana Card format
  const isValidGhanaCard = () => {
    const prefix = formData.ghana_card_prefix || '';
    const middle = formData.ghana_card_middle || '';
    const suffix = formData.ghana_card_suffix || '';
    
    // Must have prefix (3 letters), middle (9 digits), and suffix (1 digit)
    return prefix.length === 3 && 
           middle.length === 9 && 
           suffix.length === 1;
  };

  // Handle document type change - clear Ghana Card fields when changing
  const handleDocumentTypeChange = (value) => {
    onChange('document_type', value);
    // Clear Ghana Card fields when changing document type
    onChange('ghana_card_prefix', '');
    onChange('ghana_card_middle', '');
    onChange('ghana_card_suffix', '');
    onChange('card_number', '');
  };

  return (
    <div className="space-y-4">
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> Document upload is optional. You can skip this step and provide documents later.
        </p>
      </div>
      <div className="space-y-4">
        <SelectField 
          placeholder="Document Type (Optional)" 
          value={formData.document_type} 
          onChange={handleDocumentTypeChange} 
          options={documentTypeOptions} 
          delay={0.3} 
        />
        
        {/* Conditional Card Number Input */}
        {formData.document_type === 'ghana_card' ? (
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Ghana Card Number</label>
            <div className="flex items-center gap-2">
              {/* Prefix Input (3 letters) */}
              <input
                type="text"
                placeholder="GHA"
                value={formData.ghana_card_prefix || ''}
                onChange={handleGhanaCardPrefixChange}
                maxLength={3}
                className="w-20 px-3 py-3 text-sm text-center rounded-xl border border-gray-200 outline-none bg-white/95 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 uppercase"
              />
              <span className="text-gray-400">-</span>
              
              {/* Middle Input (9 digits) */}
              <input
                type="text"
                placeholder="123456789"
                value={formData.ghana_card_middle || ''}
                onChange={handleGhanaCardMiddleChange}
                maxLength={9}
                className="w-32 px-3 py-3 text-sm text-center rounded-xl border border-gray-200 outline-none bg-white/95 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
              />
              <span className="text-gray-400">-</span>
              
              {/* Suffix Input (1 digit) */}
              <input
                type="text"
                placeholder="1"
                value={formData.ghana_card_suffix || ''}
                onChange={handleGhanaCardSuffixChange}
                maxLength={1}
                className="w-12 px-3 py-3 text-sm text-center rounded-xl border border-gray-200 outline-none bg-white/95 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
              />
            </div>
            
            {/* Validation Feedback */}
            {(formData.ghana_card_prefix || formData.ghana_card_middle || formData.ghana_card_suffix) && (
              <div className="space-y-1">
                {!isValidGhanaCard() && (
                  <p className="text-xs text-red-500">
                    Please complete all fields: 3 letters, 9 digits, 1 digit
                  </p>
                )}
                {isValidGhanaCard() && (
                  <p className="text-xs text-green-600">
                    ✓ Valid Ghana Card: {getFullGhanaCardNumber()}
                  </p>
                )}
              </div>
            )}
          </div>
        ) : (
          <InputField 
            type="text" 
            placeholder="Card Number (Optional)" 
            value={formData.card_number} 
            onChange={(e) => onChange('card_number', e.target.value)} 
            delay={0.35} 
          />
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="flex overflow-hidden relative flex-col justify-center items-center p-4 text-center rounded-xl border-2 border-gray-300 border-dashed transition-colors cursor-pointer hover:border-emerald-400 bg-white/80">
          {formData.idFrontPreview ? (
            <img src={formData.idFrontPreview} alt="ID Front Preview" className="object-cover w-full h-40 rounded-lg" />
          ) : (
            <>
              <FileText size={36} className="mb-2 text-gray-400" />
              <p className="text-sm font-medium text-gray-700">ID Front (Optional)</p>
              <p className="mt-1 text-xs text-gray-500">Click to upload</p>
            </>
          )}
          <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0], 'front')} />
        </label>
        <label className="flex overflow-hidden relative flex-col justify-center items-center p-4 text-center rounded-xl border-2 border-gray-300 border-dashed transition-colors cursor-pointer hover:border-emerald-400 bg-white/80">
          {formData.idBackPreview ? (
            <img src={formData.idBackPreview} alt="ID Back Preview" className="object-cover w-full h-40 rounded-lg" />
          ) : (
            <>
              <FileText size={36} className="mb-2 text-gray-400" />
              <p className="text-sm font-medium text-gray-700">ID Back (Optional)</p>
              <p className="mt-1 text-xs text-gray-500">Click to upload</p>
            </>
          )}
          <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0], 'back')} />
        </label>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <InputField type="date" placeholder="Issue Date (Optional)" value={formData.issue_date} onChange={(e) => onChange('issue_date', e.target.value)} delay={0.4} />
        <InputField type="date" placeholder="Expiry Date (Optional)" value={formData.expiry_date} onChange={(e) => onChange('expiry_date', e.target.value)} delay={0.45} />
      </div>
    </div>
  );
}


